import { useMemo, useSyncExternalStore } from 'react';

const emptySubscribe = () => () => {};
const getBrowserSnapshot = () => true;
const getServerSnapshot = () => false;

/**
 * «Мы уже в браузере?» — для поддеревьев, которые нельзя рендерить на сервере.
 *
 * <p>Раньше это был useState + изоморфный (layout) эффект: на маунте ставил `true`, а в cleanup
 * возвращал `false`. Из-за cleanup хук стал усилителем каскадов. Потребители гейтят целые
 * поддеревья через `{isBrowser ? … : null}`, поэтому любое скрытие/раскрытие через Suspense
 * (скажем, когда запрос поста отменяется и перезапускается) роняло гейт в `false`, поддерево
 * удалялось, эффект снова ставил `true`, поддерево монтировалось заново — и внутри всё
 * повторялось уровнем глубже. Вложенные синхронные обновления упирались в лимит React и давали
 * «Minified React error #185: Maximum update depth exceeded» — на проде 04–05.08.2026.
 *
 * <p>useSyncExternalStore решает ту же задачу без состояния и эффектов: на сервере снапшот
 * `false`, в браузере `true`, повторные рендеры не порождают обновлений вообще. Гидрация
 * по-прежнему корректна — первый клиентский рендер совпадает с серверным.
 */
export const useSSR = () => {
  const inBrowser = useSyncExternalStore(emptySubscribe, getBrowserSnapshot, getServerSnapshot);

  return useMemo(
    () => ({
      isBrowser: inBrowser,
      isServer: !inBrowser,
      canUseWorkers: typeof Worker !== 'undefined',
      canUseEventListeners: inBrowser && !!window.addEventListener,
      canUseViewport: inBrowser && !!window.screen,
    }),
    [inBrowser]
  );
};
